import { useEffect, useState } from "react";
import { api } from "@/lib/nexus";
import { useAuth } from "@/context/AuthContext";
import {
  KeyRound, Plus, Copy, Check, Trash2, Loader2, Activity,
  ShieldAlert, Terminal, X,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return s; }
}

export default function ApiKeys() {
  const { canWrite } = useAuth();
  const [keys, setKeys] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState(null); // {name, key}
  const [copied, setCopied] = useState(false);

  const load = () =>
    Promise.all([api.get("/keys"), api.get("/keys/usage")]).then(([k, u]) => {
      setKeys(k.data.items || []);
      setUsage(u.data);
      setLoading(false);
    });

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const r = await api.post("/keys", { name: newName.trim() });
      setRevealed({ name: r.data.name, key: r.data.key });
      setNewName("");
      setShowCreate(false);
      await load();
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this key? Any integration using it will stop working immediately.")) return;
    await api.delete(`/keys/${id}`);
    load();
  };

  const copyKey = () => {
    navigator.clipboard.writeText(revealed.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const maxDay = Math.max(1, ...(usage?.series || []).map((s) => s.count));

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="panel-title">PUBLIC API // ACCESS &amp; USAGE</div>
          <h1 className="font-rajdhani font-bold text-2xl tracking-tight mt-1">
            Tentacles <span className="text-octopi-purple">API keys</span>
          </h1>
          <p className="text-[13px] text-nexus-text-2 mt-1 max-w-2xl">
            Issue keys for your agents &amp; integrations to query on-chain intel programmatically.
            Every call is metered and rate-limited. Revoke instantly if a key leaks.
          </p>
        </div>
        {canWrite && (
          <button data-testid="apikeys-create-button" onClick={() => setShowCreate(true)}
            className="text-[11px] font-mono uppercase tracking-widest text-octopi-purple border border-octopi-purple/50 hover:bg-octopi-purple/10 px-4 py-2 flex items-center gap-1.5">
            <Plus size={13} /> New API Key
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-nexus-text-2"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : (
        <>
          {/* Usage summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "All-time calls", value: usage?.all_time ?? 0, testid: "usage-total-alltime" },
              { label: "Last 7 days", value: usage?.total_7d ?? 0, testid: "usage-total-7d" },
              { label: "Active keys", value: usage?.active_keys ?? 0, testid: "usage-active-keys" },
              { label: "Total keys", value: usage?.total_keys ?? 0, testid: "usage-total-keys" },
            ].map((c) => (
              <div key={c.label} className="panel p-3">
                <div className="panel-title">{c.label}</div>
                <div className="font-rajdhani font-bold text-3xl text-nexus-cyan mt-1" data-testid={c.testid}>{c.value}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {/* 7-day chart */}
            <div className="panel brackets p-4 lg:col-span-2">
              <span className="b1" /><span className="b2" />
              <div className="panel-title mb-3"><Activity size={12} className="inline mr-1" /> Calls · last 7 days</div>
              <div className="flex items-end gap-2 h-32">
                {(usage?.series || []).map((s) => (
                  <div key={s.day} className="flex-1 flex flex-col items-center gap-1 group">
                    <div className="w-full bg-octopi-purple/30 group-hover:bg-octopi-purple/60 border-t border-octopi-purple transition-colors relative"
                      style={{ height: `${(s.count / maxDay) * 100}%`, minHeight: s.count ? "3px" : "0" }}>
                      <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-[9px] font-mono text-nexus-text-3 opacity-0 group-hover:opacity-100">{s.count}</span>
                    </div>
                    <span className="text-[9px] font-mono text-nexus-text-3">{s.day.slice(5)}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Top endpoints */}
            <div className="panel p-4">
              <div className="panel-title mb-2">Top endpoints</div>
              {(usage?.top_endpoints || []).length === 0 && <div className="text-[12px] text-nexus-text-3 font-mono">No calls yet.</div>}
              <div className="space-y-1.5">
                {(usage?.top_endpoints || []).map((e) => (
                  <div key={e.endpoint} className="flex justify-between text-[11px] font-mono">
                    <span className="text-nexus-text-2 truncate">{e.endpoint}</span>
                    <span className="text-nexus-cyan ml-2">{e.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Keys table */}
          <div className="panel p-0 overflow-hidden" data-testid="apikeys-table">
            <div className="panel-title p-3 border-b border-nexus-border"><KeyRound size={12} className="inline mr-1" /> Your keys ({keys.length})</div>
            {keys.length === 0 ? (
              <div className="p-6 text-center text-[13px] text-nexus-text-3 font-mono">No keys yet. Create one to start using the API.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[12px] font-mono">
                  <thead>
                    <tr className="text-nexus-text-3 text-[10px] uppercase tracking-widest border-b border-nexus-border">
                      <th className="text-left px-3 py-2">Name</th>
                      <th className="text-left px-3 py-2">Key</th>
                      <th className="text-right px-3 py-2">Calls</th>
                      <th className="text-left px-3 py-2 pl-4">Last used</th>
                      <th className="text-left px-3 py-2">Created</th>
                      <th className="text-right px-3 py-2">Status</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {keys.map((k) => (
                      <tr key={k.id} className="border-b border-nexus-border/40 hover:bg-nexus-panel-2/40">
                        <td className="px-3 py-2 text-nexus-text">{k.name}</td>
                        <td className="px-3 py-2 text-nexus-text-2">{k.key_prefix}…••••</td>
                        <td className="px-3 py-2 text-right">{k.call_count || 0}</td>
                        <td className="px-3 py-2 pl-4 text-nexus-text-3">{fmtDate(k.last_used_at)}</td>
                        <td className="px-3 py-2 text-nexus-text-3">{fmtDate(k.created_at)}</td>
                        <td className="px-3 py-2 text-right">
                          {k.revoked
                            ? <span className="text-[9px] uppercase tracking-widest text-nexus-rose border border-nexus-rose/40 px-1.5 py-0.5">revoked</span>
                            : <span className="text-[9px] uppercase tracking-widest text-nexus-cyan border border-nexus-cyan/40 px-1.5 py-0.5">active</span>}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {!k.revoked && canWrite && (
                            <button data-testid={`apikeys-revoke-${k.id}`} onClick={() => revoke(k.id)}
                              className="text-nexus-text-3 hover:text-nexus-rose" title="Revoke"><Trash2 size={14} /></button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Usage snippet */}
          <div className="panel p-4">
            <div className="panel-title mb-2"><Terminal size={12} className="inline mr-1" /> Using your key</div>
            <pre className="text-[11px] font-mono text-nexus-text-2 bg-nexus-bg border border-nexus-border p-3 overflow-x-auto whitespace-pre">{`curl "${API_BASE}/api/agents?limit=5" \\
  -H "X-API-Key: tnt_live_your_key_here"

# or as a bearer token
curl "${API_BASE}/api/graph/query" -X POST \\
  -H "Authorization: Bearer tnt_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"query":"high risk agents on Base"}'`}</pre>
          </div>
        </>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowCreate(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={create} className="panel brackets p-5 w-full max-w-sm" data-testid="apikeys-create-modal">
            <span className="b1" /><span className="b2" />
            <div className="flex items-center justify-between mb-3">
              <div className="panel-title"><KeyRound size={12} className="inline mr-1" /> New API Key</div>
              <button type="button" onClick={() => setShowCreate(false)} className="text-nexus-text-3 hover:text-nexus-text"><X size={15} /></button>
            </div>
            <label className="block">
              <span className="panel-title">Key name</span>
              <input data-testid="apikeys-name-input" autoFocus required value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. production-agent, research-bot"
                className="mt-1 w-full bg-nexus-bg border border-nexus-border px-3 py-2 text-[13px] font-mono focus:border-octopi-purple/60 outline-none" />
            </label>
            <button data-testid="apikeys-create-submit" type="submit" disabled={creating}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-octopi-purple/15 border border-octopi-purple/60 text-octopi-purple hover:bg-octopi-purple/25 py-2.5 text-[12px] font-mono uppercase tracking-widest disabled:opacity-50">
              {creating && <Loader2 size={13} className="animate-spin" />} Generate Key
            </button>
          </form>
        </div>
      )}

      {/* Reveal-once modal */}
      {revealed && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="panel brackets p-5 w-full max-w-lg" data-testid="apikeys-reveal">
            <span className="b1" /><span className="b2" />
            <div className="flex items-center gap-2 text-nexus-amber mb-2">
              <ShieldAlert size={15} />
              <div className="panel-title" style={{ color: "#FFB000" }}>Copy your key now — shown only once</div>
            </div>
            <p className="text-[12px] text-nexus-text-2 mb-3">Key <span className="text-nexus-text">"{revealed.name}"</span> created. Store it securely; you won't be able to see it again.</p>
            <div className="flex items-center gap-2 bg-nexus-bg border border-octopi-purple/50 px-3 py-2.5">
              <code className="flex-1 text-[12px] font-mono text-octopi-purple break-all" data-testid="apikeys-reveal-value">{revealed.key}</code>
              <button data-testid="apikeys-copy-button" onClick={copyKey} className="text-nexus-text-2 hover:text-nexus-cyan shrink-0">
                {copied ? <Check size={16} className="text-nexus-cyan" /> : <Copy size={16} />}
              </button>
            </div>
            <button onClick={() => setRevealed(null)}
              className="mt-4 w-full border border-nexus-border hover:border-nexus-cyan/60 py-2 text-[12px] font-mono uppercase tracking-widest text-nexus-text-2">
              Done — I've saved it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
